/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { PropertyService } from '@vsolv/packages/properties/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { BehaviorSubject, map, shareReplay, switchMap } from 'rxjs';
import { PLAN_TERM_USAGE_LIMIT_CONFIG } from '../../config/plan-term-usage-limit.config';
import { FeatureFlagsService, NEW_CLAIMS_FLOW_FLAG, PolicyService } from '../../services';

// A new limit starts empty, so Save waits for a number or a value to read it from.
const NEW_USAGE_LIMIT = { blocks: [], defaultValue: null } as unknown as Conditions.Rule<number>;

const toNumber = (value: unknown) => (value === null || value === undefined || value === '' ? 0 : Number(value));

// Titles are staff-entered and the toast and header render HTML.
const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
  );

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-plan-term-conditions-dialog',
  templateUrl: './plan-term-conditions.dialog.html',
  standalone: false,
})
export class PlanTermConditionsDialog {
  constructor(
    private router: Router,
    private toastSvc: ToastService,
    private policySvc: PolicyService,
    private formBuilder: FormBuilder,
    private propertySvc: PropertyService,
    featureFlagsSvc: FeatureFlagsService
  ) {
    featureFlagsSvc
      .isEnabledState(NEW_CLAIMS_FLOW_FLAG)
      .pipe(takeUntilDestroyed())
      .subscribe(on => (this.newClaimsFlow = on === true));
  }

  /** Usage limits feed the new claims flow's eligibility check; with the tenant's switch off they're hidden. */
  newClaimsFlow = false;

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @PropertyListener('policy') policy$ = new BehaviorSubject<Policy.Model | null>(null);
  @Input() policy: Policy.Model | null = null;

  @PropertyListener('plan') plan$ = new BehaviorSubject<Policy.Plan.Model | null>(null);
  @Input() plan: Policy.Plan.Model | null = null;

  readonly usageLimitConfig = PLAN_TERM_USAGE_LIMIT_CONFIG;

  selectedTerm: Policy.PolicyTerm | null = null;
  saving = false;
  addCondition = false;
  addUsageLimit = false;
  draft = Policy.Status.DRAFT;

  form = this.formBuilder.group({
    termConditions: [{ comparison: Conditions.Comparison.AND, conditions: [] } as Conditions.ConditionGroup],
    usageDimensionId: [null as string | null],
    usageLimit: [NEW_USAGE_LIMIT],
  });

  propertySet$ = this.policy$.pipe(
    switchMap(async policy => {
      if (!policy) return null;
      return await this.propertySvc.retrievePropertySet(policy.propertySetId);
    }),
    shareReplay(1)
  );

  // Usage is read from number dimensions (odometer, engine hours…), like the claim's reading at loss.
  usageDimensions$ = this.propertySet$.pipe(
    map(set =>
      (set?.dimensions ?? [])
        .filter(assignment => assignment.dimension?.isScalar)
        .map(assignment => ({
          id: assignment.dimensionId,
          title: assignment.dimension?.title ?? assignment.dimensionId,
        }))
    )
  );

  get headerSubtitle() {
    const { dialog } = this.usageLimitConfig;
    if (!this.selectedTerm || !this.policy) {
      return this.newClaimsFlow ? dialog.fallbackSubtitle : dialog.conditionsFallbackSubtitle;
    }
    const subtitle = this.newClaimsFlow ? dialog.subtitle : dialog.conditionsSubtitle;
    return subtitle(escapeHtml(this.selectedTerm.title), escapeHtml(this.policy.title));
  }

  /** Usage limits are only checked in the new claims flow, on policies with the claim eligibility check on. */
  get usageLimitsOn() {
    return this.newClaimsFlow && !!this.policy?.checkClaimEligibility;
  }

  get savedLimitWhileOff() {
    return !this.usageLimitsOn && !!this.selectedTerm && !!this.plan?.termUsageLimits?.[this.selectedTerm.id];
  }

  /** Why the usage limit can't be saved yet, shown next to Save; null when it can. */
  get usageLimitProblem(): string | null {
    if (!this.usageLimitsOn || !this.addUsageLimit) return null;
    if (!this.form.value.usageDimensionId) return this.usageLimitConfig.problems.reading;
    if (this.form.controls.usageLimit.invalid || !this.usageLimitFilledIn())
      return this.usageLimitConfig.problems.values;
    return null;
  }

  get usageLimitInvalid() {
    return !!this.usageLimitProblem;
  }

  readingTitle(dimensions: { id: string; title: string }[], id: string | null | undefined) {
    return dimensions.find(dimension => dimension.id === id)?.title ?? '';
  }

  // A cleared number comes back as '', which must not save as a limit of 0.
  private usageLimitFilledIn() {
    const rule = this.form.value.usageLimit;
    const filled = (value: unknown, path?: string | null) =>
      !!path || (value !== null && value !== undefined && value !== '');

    return (
      !!rule &&
      filled(rule.defaultValue, rule.valuePropertyPath) &&
      (rule.blocks ?? []).every(block => filled(block.value, block.valuePropertyPath))
    );
  }

  open(term: Policy.PolicyTerm) {
    if (!this.plan) return;

    this.selectedTerm = term;

    const termConditions = this.plan.termConditions?.[term.id] ?? null;
    const usageLimit = this.plan.termUsageLimits?.[term.id] ?? null;
    this.form.reset({
      termConditions,
      usageDimensionId: usageLimit?.dimensionId ?? null,
      usageLimit: usageLimit?.limit ?? NEW_USAGE_LIMIT,
    });

    this.addCondition = !!termConditions;
    this.addUsageLimit = !!usageLimit;
    this.dialog.open();
  }

  close() {
    this.dialog.close();
    this.reset();
  }

  /** Also runs when the dialog closes on Esc, the backdrop or the header's X, so no term's edits carry over. */
  reset() {
    this.selectedTerm = null;
    this.addCondition = false;
    this.addUsageLimit = false;
    this.form.reset({
      termConditions: null,
      usageDimensionId: null,
      usageLimit: NEW_USAGE_LIMIT,
    });
  }

  removeUsageLimit() {
    this.addUsageLimit = false;
    this.form.patchValue({ usageDimensionId: null, usageLimit: NEW_USAGE_LIMIT });
  }

  /** The limit as saved: numbers, not the masked input's strings. */
  private usageLimitValue(): Policy.Plan.UsageLimit | null {
    const { usageDimensionId, usageLimit } = this.form.value;
    if (!this.addUsageLimit || !usageDimensionId || !usageLimit) return null;

    return {
      dimensionId: usageDimensionId,
      limit: {
        blocks: (usageLimit.blocks ?? []).map(block => ({ ...block, value: toNumber(block.value) })),
        defaultValue: toNumber(usageLimit.defaultValue),
        valuePropertyPath: usageLimit.valuePropertyPath ?? null,
      },
    };
  }

  async save() {
    if (!this.policy || !this.plan || !this.selectedTerm || this.usageLimitInvalid) return;

    this.saving = true;

    const policyId = this.policy.id;
    const planId = this.plan.id;
    const termId = this.selectedTerm.id;
    const conditions = this.form.value.termConditions;

    const dto: Policy.Plan.UpdatePolicyPlanTermRequest = {
      canCreateNewVersion: true,
      termConditions: conditions?.conditions?.length ? conditions : null,
      // Left out while the check is off, so a saved limit stays as it is.
      ...(this.usageLimitsOn ? { usageLimit: this.usageLimitValue() } : {}),
    };

    let response: Policy.Plan.UpdatePolicyPlanResponse | null = null;

    try {
      response = await this.policySvc.updatePlanTerm(planId, termId, policyId, dto);
    } catch (error) {
      const nextTarget = await this.resolvePlanVersionFromError(error);
      if (nextTarget) {
        this.toastSvc.show({
          type: 'info',
          title: this.usageLimitConfig.toasts.newVersionTitle,
          text: 'Policy has been updated to a new version as draft.',
        });
        try {
          response = await this.policySvc.updatePlanTerm(nextTarget.plan.id, termId, nextTarget.policy.id, dto);
        } catch (retryError) {
          this.handleRequestError(retryError);
          return;
        }
      } else {
        this.handleRequestError(error);
        return;
      }
    }

    if (response) {
      const termTitle = escapeHtml(this.selectedTerm.title);
      if (this.policy.id === response.policyId) {
        this.toastSvc.show({
          type: 'success',
          title: this.usageLimitConfig.toasts.savedTitle,
          text: this.usageLimitConfig.toasts.saved(termTitle),
        });
        await this.refreshPlanAndPolicy(response.id, response.policyId);
      } else {
        this.toastSvc.show({
          type: 'success',
          title: this.usageLimitConfig.toasts.newVersionTitle,
          text: this.usageLimitConfig.toasts.newVersion(termTitle),
        });
        // Navigate directly — PolicyGuard and PlanGuard will load the correct data for the new
        // version. Pre-refreshing policy$/plan$ before navigation causes a race condition where
        // plan$ can fire in the old component before policy$ is updated, leading to coverage
        // configs being preloaded with stale term IDs and the table being stuck in a loading state.
        await this.navigateTo('/policies/' + response.policyId + '/plans/' + response.id);
      }

      this.close();
    }

    this.saving = false;
  }

  async navigateTo(path?: string) {
    if (path) await this.router.navigate([`${path}`]);
  }

  private async refreshPlanAndPolicy(planId: string, policyId: string) {
    await Promise.all([
      this.policySvc.refreshPolicy(policyId),
      this.policySvc.refreshPlan({ id: planId, policyId } as Policy.Plan.Model, {
        includeCoverageConfigs: false,
        includeFees: false,
        includeCoverageConfigBlocks: false,
      }),
    ]);
  }

  private handleRequestError(error: any) {
    const message = error?.error?.message || 'An unexpected error occurred.';
    const status = error?.status;

    let text = message || 'An unexpected error occurred. Please try again.';

    switch (status) {
      case 400:
      case 422:
        text = message || 'Please review the term conditions and try again.';
        break;
      case 403:
        text = 'You do not have permission to edit plan term conditions.';
        break;
      case 404:
        text = message || 'We could not find this plan. Refresh the policy and try again.';
        break;
      default:
        text = message || 'Something went wrong on our end. Please try again in a moment.';
        break;
    }

    this.toastSvc.show({
      type: 'error',
      title: 'Unable to save term conditions',
      text,
    });

    this.close();
    this.saving = false;
  }

  private async resolvePlanVersionFromError(error: any) {
    if (!this.policy || !this.plan) return null;
    if (error?.status !== 404) return null;

    const message = (error?.error?.message || '').toLowerCase();
    if (!message.includes('plan')) return null;

    const currentPolicy = await this.policySvc.getOne(this.policy.id);
    const succeededById = currentPolicy?.succeededById;
    if (!succeededById) return null;

    const newPolicy = await this.policySvc.getOne(succeededById);
    if (!newPolicy?.plans?.length) return null;

    const criteria = [
      (newPlan: any) => newPlan.id === this.plan?.id,
      (newPlan: any) => newPlan.title === this.plan?.title && newPlan.order === this.plan?.order,
      (newPlan: any) => newPlan.order === this.plan?.order,
      (newPlan: any) => newPlan.title === this.plan?.title,
      (newPlan: any) => newPlan.planLiabilityLimit === this.plan?.planLiabilityLimit,
    ];

    const scoredPlans = newPolicy.plans
      .map(newPlan => {
        let score = -1;
        for (let i = 0; i < criteria.length; i++) {
          if (criteria[i](newPlan)) {
            score = i;
            break;
          }
        }
        return { plan: newPlan, score };
      })
      .filter(p => p.score !== -1);

    const replacementPlan = scoredPlans.length
      ? scoredPlans.reduce((best, current) => (current.score < best.score ? current : best)).plan
      : null;

    if (!replacementPlan) return null;

    return { policy: newPolicy, plan: replacementPlan };
  }
}
